import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { formatBytes } from '../../../../utils/helpers';
import { forkJoin, Subject, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ImageSelectionEvent, SignClickEvent, SigntrustCertificate, PageInfo, SignImageType, MessageType, SignatureProfile, SignatureImage, InfoItems, PageType } from '@models/index.model';
import { FeedMessageService, SigntrustService, SigntrustUtilsService } from '@services/index.service';
import { DialogConfirmCancelComponent } from '@shared/index.shared';
import { TranslateService } from '@ngx-translate/core';
import { DialogSelectCertificateComponent } from '@pages/index.pages';
import { environment } from '@environments/environment';

@Component({
  selector: 'lib-dialog-edit-signature-config',
  templateUrl: './dialog-edit-signature-config.component.html',
  styleUrls: ['./dialog-edit-signature-config.component.scss'],
  standalone: false,
})
export class EditSignatureConfigComponent implements OnInit, OnDestroy {
  public isMobile: boolean;
  @ViewChild(MatStepper) stepper: MatStepper;
  // CERTIFICATE STEP
  public sendingInfo: boolean = false;
  public certificateInfo: SigntrustCertificate;
  // IMAGE STEP
  public imageSelectionEvent: ImageSelectionEvent;
  public signClickEvent = SignClickEvent;
  // BUTTONS
  public enableNext: boolean = false;
  public showSign: boolean = false;
  public showContinue: boolean = false;
  public resetClicked$: Subject<void> = new Subject();
  private destroy$: Subject<void> = new Subject();
  public pageInfo: PageInfo;
  @ViewChild('rangePages') rangePages: ElementRef;
  public theme: string;
  public SignImageType = SignImageType;
  public PageType = PageType;
  public radioSignatureImage: boolean = false;
  public radioSignatureImagePages: PageType = PageType.CURRENT;
  public defaultImageFile: File | undefined;
  public templateImageFile: any;
  public imgTemplateSelect: any;
  public sendingRequest: boolean = false;
  public metadataCertificate: boolean = false;
  public pageRangeLabel: string = undefined;
  public username: string;
  public currentLang: string;
  public actionRange: any;
  public rangeDelay: number = 300;
  public invalidRangeFormat: boolean = false;
  public invalidRangerequired: boolean = false;
  public currentDate: Date;
  allowedExtension = ['image/jpeg', 'image/jpg', 'image/png'];
  public userSignConfig: any;
  public hasSelectedCertificate: boolean = false;
  base64Image: string = '';
  public selectCertificate: SigntrustCertificate;
  public certificateList: SigntrustCertificate[];
  selectCertificateType: boolean = false;
  metadataSelection: any;
  pages: number[];
  pageType: PageType = PageType.CURRENT;
  imgInfo: any;
  blockImg: boolean = true;
  blockPages: boolean = true;
  blockCertificate: boolean = true;

  // SIGNTRUST PROFILE (new endpoints)
  public signatureProfile: SignatureProfile | null = null;
  public signatureImage: SignatureImage | null = null;
  private _hasLocalImageChange = false;
  private _initialSnapshot: any = null;
  public hasChanges: boolean = false;
  private _hasParentConfig = false;
  private _getDefaultUiConfig(): any {
    return {
      certificate: {
        certSelect: false,
        certSelectData: { certId: null }
      },
      template: {
        useCustomImg: false,
        useCustomInfo: true,
        pagesData: { pageType: PageType.CURRENT, pageRange: undefined as any },
        customInfoData: [
          { key: 'certificate', check: true, name: 'Certificado', disabled: true },
          { key: 'friendlyName', check: true, name: 'Firmado por', disabled: false },
          { key: 'date', check: true, name: 'Fecha y hora', disabled: false },
          { key: 'thumbprint', check: true, name: 'Huella digital', disabled: false }
        ]
      }
    };
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _dialog: MatDialog,
    public dialogRef: MatDialogRef<EditSignatureConfigComponent>,
    private _cdRef: ChangeDetectorRef,
    @Inject('environment') private _environment,
    private _translate: TranslateService,
    private _notify: FeedMessageService,
    private _utilsSigntrust: SigntrustUtilsService,
    private _signtrustServ: SigntrustService,
    private _utils: SigntrustUtilsService,
  ) {
    this.isMobile = environment.platform === 'mobile';
    this.currentLang = this._translate.currentLang;
    this.theme = this._utilsSigntrust.titleCaseWord(this._environment.THEME);
    this.username = this._utilsSigntrust.getLocalStorage('PersonalUserSession')?.userData?.friendlyName;
  }

  public ngOnInit(): void {
    if (!this.data) {
      this.onCancel();
      return;
    }
    this._hasParentConfig = !!this.data?.userSignConfig;
    if (this._hasParentConfig) {
      this.mapUserSignConfig(this.data);
      return;
    }
    this._loadFromStoredProfile();
  }

  private _loadFromStoredProfile(): void {
    this.sendingInfo = true;
    forkJoin({
      profile: this._signtrustServ.getSignatureProfile().pipe(
        switchMap(r => of(r.data))
      ),
      image: this._signtrustServ.getSignatureImage().pipe(
        switchMap(r => of(r.data))
      ),
    }).subscribe({
      next: ({ profile, image }) => {
        this.signatureProfile = profile ?? null;
        const ui = this._getDefaultUiConfig();
        if (profile) {
          ui.template.useCustomInfo = !!profile.includeInfo;
          ui.template.useCustomImg = !!profile.includeImage;
          ui.template.pagesData.pageType = profile.pageType ?? PageType.CURRENT;
          ui.template.pagesData.pageRange = profile.pageRange ?? undefined;
          const items = new Set(profile.infoItems || []);
          ui.template.customInfoData[0].check = items.has(InfoItems.CERTIFICATE);
          ui.template.customInfoData[1].check = items.has(InfoItems.FRIENDLYNAME);
          ui.template.customInfoData[2].check = items.has(InfoItems.DATE);
          ui.template.customInfoData[3].check = items.has(InfoItems.THUMBPRINT);
          this._translate.get(['WEB-SIGN.SIGN-IMG.LABELS.SIGN-METADATA-LIST']).subscribe(res => {
            ui.template.customInfoData[0].name = res['WEB-SIGN.SIGN-IMG.LABELS.SIGN-METADATA-LIST']['CERTIFICATE'];
            ui.template.customInfoData[1].name = res['WEB-SIGN.SIGN-IMG.LABELS.SIGN-METADATA-LIST']['SIGN-BY'];
            ui.template.customInfoData[2].name = res['WEB-SIGN.SIGN-IMG.LABELS.SIGN-METADATA-LIST']['DATE'];
            ui.template.customInfoData[3].name = res['WEB-SIGN.SIGN-IMG.LABELS.SIGN-METADATA-LIST']['THUMBPRINT'];
          });
          ui.certificate.certSelect = !!profile.hasSelectedCert;
          ui.certificate.certSelectData.certId = profile.selectedCertId ?? null;
        }
        this.userSignConfig = ui;
        this.metadataCertificate = this.userSignConfig.template.useCustomInfo;
        this.metadataSelection = this.userSignConfig.template.customInfoData;
        this.currentDate = new Date();
        this.pageType = this.userSignConfig.template.pagesData.pageType;
        this.pageRangeLabel = this.userSignConfig.template.pagesData.pageRange;
        this.pagesSignTypeChange(this.pageType);
        this.radioSignatureImage = !!this.userSignConfig.template.useCustomImg;
        if (this.radioSignatureImage) {
          const img64 = image?.imageInBase64 || '';
          this.signatureImage = image ?? null;
          if (img64) {
            const dataUrl = this._normalizeToDataUrl(img64);
            const file = this._dataUrlToFile(dataUrl, 'signature.png');
            this.defaultImageFile = file;
            this.imageSelectionChanged(file, true);
          } else {
            this.defaultImageFile = undefined;
            this.imageSelectionChanged(null, true);
          }
        } else {
          this.defaultImageFile = undefined;
          this.blockImg = false;
        }
        this.selectCertificateType = this.userSignConfig.certificate.certSelect;
        this.selectCertificate = this.data?.selectCertificate ?? null;
        this.certificateSignTypeChange(this.selectCertificateType);
        this.sendingInfo = false;
      },
      error: (err) => {
        console.error(err);
        this.userSignConfig = this._getDefaultUiConfig();
        this.metadataCertificate = this.userSignConfig.template.useCustomInfo;
        this.metadataSelection = this.userSignConfig.template.customInfoData;
        this.pageType = this.userSignConfig.template.pagesData.pageType;
        this.sendingInfo = false;
      }
    });
  }

  public mapUserSignConfig(parentData) {
    this.userSignConfig = parentData.userSignConfig;
    this.signatureProfile = parentData.signatureProfile ?? this.signatureProfile;
    this.metadataCertificate = this.userSignConfig.template.useCustomInfo;
    this.metadataSelection = this.userSignConfig.template.customInfoData;
    this.currentDate = new Date();
    this.detailsSignTypeChange(this.metadataCertificate);
    this.radioSignatureImage = !!this.userSignConfig.template.useCustomImg;
    this.defaultImageFile = parentData.defaultImageFile ?? this.defaultImageFile;
    if (this.radioSignatureImage) {
      if (this.defaultImageFile) {
        this.imageSelectionChanged(this.defaultImageFile, true);
      } else {
        this._loadSignatureImage();
      }
    } else {
      this.blockImg = false;
    }
    this.pageInfo = {
      currentPage: parentData.currentPage,
      totalPages: parentData.totalPages,
      pagesRange: [],
    };
    this.pageType = this.userSignConfig.template.pagesData.pageType;
    this.pageRangeLabel = this.userSignConfig.template.pagesData.pageRange;
    this.pagesSignTypeChange(this.pageType);
    this.selectCertificateType = this.userSignConfig.certificate.certSelect;
    this.selectCertificate = parentData.selectCertificate;
    this.certificateSignTypeChange(this.selectCertificateType);
    this._initialSnapshot = this._buildSnapshot();
    this.hasChanges = false;
  }

  private _syncBlocksVisibility(): void {
    if (!this.radioSignatureImage && !this.metadataCertificate) {
      this.blockPages = false;
    }
    if (!this.radioSignatureImage) {
      this.blockImg = false;
    }
    if (!this.selectCertificateType) {
      this.blockCertificate = false;
    }
  }

  private _buildSnapshot(): any {
    return {
      includeInfo: !!this.metadataCertificate,
      infoItems: (this.metadataSelection || []).map(x => !!x.check),
      includeImage: !!this.radioSignatureImage,
      pageType: this.pageType,
      pageRange: (this.pageRangeLabel ?? '').trim(),
      hasSelectedCert: !!this.selectCertificateType,
      selectedCertId: this.selectCertificateType ? (this.selectCertificate?.certificateHandler ?? null) : null,
      localImageChanged: !!this._hasLocalImageChange,
    };
  }

  private _updateHasChanges(): void {
    if (!this._initialSnapshot) {
      this.hasChanges = false;
      return;
    }
    const now = this._buildSnapshot();
    this.hasChanges = JSON.stringify(now) !== JSON.stringify(this._initialSnapshot);
  }

  public signDetailschage() {
    this._updateHasChanges();
  }

  public onCancel(): void {
    this._dialog
      .open(DialogConfirmCancelComponent, {
        width: '400px',
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.dialogRef.close(false);
        }
      });
  }

  public onCloseEditSign(type): void {
    // details sign
    this.userSignConfig.template.useCustomInfo = this.metadataCertificate;
    this.userSignConfig.template.customInfoData = this.metadataSelection;
    // visual sign
    this.userSignConfig.template.useCustomImg = this.radioSignatureImage;
    // pages sign
    this.userSignConfig.template.pagesData.pageRange = this.pageRangeLabel;
    this.userSignConfig.template.pagesData.pageType = this.pageType;
    // certificate sign
    this.userSignConfig.certificate.certSelect = this.selectCertificateType;
    if (this.userSignConfig.certificate.certSelect) {
      this.userSignConfig.certificate.certSelectData.certId = this.selectCertificate.certificateHandler;
    }
    if (type === 'CONTINUE') {
      this.dialogRef.close({
        type: type,
        pages: this.pages,
        defaultImageFile: this.defaultImageFile,
        selectCertificate: this.selectCertificate,
        userSignConfig: this.userSignConfig,
        signatureProfile: this.signatureProfile,
        signatureImage: this.signatureImage,
      });
    } else if (type === 'SAVE') {
      this._saveSignatureConfiguration();
    }
  }

  private _saveSignatureConfiguration(): void {
    this.sendingInfo = true;
    this.userSignConfig.template.useCustomInfo = this.metadataCertificate;
    this.userSignConfig.template.customInfoData = this.metadataSelection;
    this.userSignConfig.template.useCustomImg = this.radioSignatureImage;
    this.userSignConfig.template.pagesData.pageRange = this.pageRangeLabel;
    this.userSignConfig.template.pagesData.pageType = this.pageType;
    this.userSignConfig.certificate.certSelect = this.selectCertificateType;
    if (this.userSignConfig.certificate.certSelect) {
      this.userSignConfig.certificate.certSelectData.certId = this.selectCertificate?.certificateHandler;
    }
    const profile = this._mapUiConfigToSignatureProfile();
    this._signtrustServ.setSignatureProfile(profile).pipe(
      switchMap(() => this._signtrustServ.getSignatureProfile()),
      map(r => r.data),
      switchMap((freshProfile) => {
        this.signatureProfile = freshProfile;
        return this._buildSaveImageObservable().pipe(
          switchMap(() => this._signtrustServ.getSignatureImage()),
          map(r => r.data),
          catchError(() => of(null)),
          map((freshImg) => ({ freshProfile, freshImg }))
        );
      }),
      catchError((err) => {
        console.error(err);
        return of({ freshProfile: null, freshImg: null });
      })
    )
    .subscribe({
      next: ({ freshProfile, freshImg }) => {
        if (freshProfile) this.signatureProfile = freshProfile;
        if (freshImg !== null) this.signatureImage = freshImg;
        if (this.signatureProfile?.includeImage && this.signatureImage?.imageInBase64) {
          const dataUrl = this._normalizeToDataUrl(this.signatureImage.imageInBase64);
          this.defaultImageFile = this._dataUrlToFile(dataUrl, 'signature.png');
        }
        this._hasLocalImageChange = false;
        this.sendingInfo = false;
        this._initialSnapshot = this._buildSnapshot();
        this.hasChanges = false;
        this.dialogRef.close({
          type: 'SAVE',
          pages: this.pages,
          defaultImageFile: this.defaultImageFile,
          selectCertificate: this.selectCertificate,
          userSignConfig: this.userSignConfig,
          signatureProfile: this.signatureProfile,
          signatureImage: this.signatureImage,
        });
      },
      error: (err) => {
        this.sendingInfo = false;
        console.error(err);
      }
    });
  }

  private _buildSaveImageObservable() {
    if (!this.radioSignatureImage) {
      return of({ data: this.signatureImage } as any);
    }
    if (!this._hasLocalImageChange || !this.defaultImageFile) {
      return of({ data: this.signatureImage } as any);
    }
    return this._utils.fileToBase64(this.defaultImageFile).pipe(
      switchMap((dataUrl: string) => {
        const raw = this._stripDataUrlPrefix(dataUrl);
        return this._signtrustServ.setSignatureImage({ imageInBase64: raw });
      }),
    );
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // DETAILS
  public detailsSignTypeChange(type) {
    this.userSignConfig.template.useCustomInfo = type;
    this.username = this._utilsSigntrust.getLocalStorage('PersonalUserSession')?.userData?.friendlyName;
    this.currentDate = new Date();
    if (type) {
      this.metadataSelection[0].check = true;
    }
    if (!type && Array.isArray(this.metadataSelection)) {
      this.metadataSelection.forEach(item => {
        if (!item.disabled) item.check = false;
      });
    }
    this._updateHasChanges();
  }

  // CERTIFICATE
  public certificateSignTypeChange(type) {
    if (type) {
      if (this.selectCertificate) {
        this.blockCertificate = false;
      } else {
        this.blockCertificate = true;
      }
    } else {
      this.blockCertificate = false;
      this.selectCertificate = null;
    }
    this._syncBlocksVisibility();
    this._updateHasChanges();
  }

  public onSelectCertificate() {
    const dialogRef = this._dialog.open(DialogSelectCertificateComponent, {
      width: '900px',
      disableClose: true,
      data: {
        type: 'current',
        selectCertificate: this.selectCertificate,
      },
    });

    return dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.selectCertificate = res.selectCertificate;
        this.blockCertificate = false;
        this._syncBlocksVisibility();
        this._updateHasChanges();
      }
    });
  }

  // PAGES
  public pagesSignTypeChange(selection: PageType): void {
    switch (selection) {
      case PageType.RANGE: {
        if (this.pageRangeLabel) {
          this.initValidatePagesRange(this.pageRangeLabel);
          this.blockPages = false;
        } else {
          this.blockPages = true;
          setTimeout(() => {
            this.rangePages.nativeElement.focus();
            this._cdRef.detectChanges();
          }, 100);
        }
        break;
      }
      default:
        this.blockPages = false;
        break;
    }
    this._syncBlocksVisibility();
    this._updateHasChanges();
  }

  public initValidatePagesRange(value) {
    this.stopValidatePagesRange();
    value = value.replace(' ', '').trim();
    if (value) {
      this.actionRange = setTimeout(() => {
        this.validatePagesRange(value);
        this.invalidRangerequired = false;
      }, this.rangeDelay);
    } else {
      this.invalidRangerequired = true;
      this.pageInfo.pagesRange = undefined;
      this.blockPages = true;
    }
    this._updateHasChanges();
  }

  public stopValidatePagesRange() {
    clearTimeout(this.actionRange);
  }

  public validatePagesRange(pagesRange) {
    if (pagesRange != '') {
      try {
        this.pageInfo.pagesRange = this._utilsSigntrust.parsePageRange(pagesRange, this.pageInfo.totalPages);
        if (!this.pageInfo.pagesRange) {
          this.invalidRangeFormat = true;
          this.blockPages = true;
        } else {
          this.invalidRangeFormat = false;
          this.blockPages = false;
        }
      } catch (error) {
        console.error(error);
      }
    }
    this._updateHasChanges();
  }

  private _loadSignatureImage(): void {
    this._signtrustServ.getSignatureImage().subscribe({
      next: (res) => {
        this.signatureImage = res.data;
        const img64 = this.signatureImage?.imageInBase64 || '';
        if (img64) {
          const dataUrl = this._normalizeToDataUrl(img64);
          const file = this._dataUrlToFile(dataUrl, 'signature.png');
          this.defaultImageFile = file;
          this.imageSelectionChanged(file, true);
        } else {
          this.defaultImageFile = undefined;
          this.imageSelectionChanged(null, true);
        }
      },
      error: () => {
        this.signatureImage = null;
        this.defaultImageFile = undefined;
        this.imageSelectionChanged(null, true);
      },
    });
  }

  private _normalizeToDataUrl(value: string): string {
    if (!value) return '';
    if (value.startsWith('data:')) return value;
    return `data:image/png;base64,${value}`;
  }

  private _stripDataUrlPrefix(value: string): string {
    if (!value) return '';
    const idx = value.indexOf(',');
    return idx >= 0 ? value.substring(idx + 1) : value;
  }

  private _dataUrlToFile(dataUrl: string, filename: string): File {
    const arr = dataUrl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const bstr = atob(arr[1] || '');
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  }

  private _mapUiConfigToSignatureProfile(): SignatureProfile {
    const includeInfo = !!this.metadataCertificate;
    let infoItems: InfoItems[] = [];
    if (includeInfo) {
      if (this.metadataSelection?.[0]?.check) infoItems.push(InfoItems.CERTIFICATE);
      if (this.metadataSelection?.[1]?.check) infoItems.push(InfoItems.FRIENDLYNAME);
      if (this.metadataSelection?.[2]?.check) infoItems.push(InfoItems.DATE);
      if (this.metadataSelection?.[3]?.check) infoItems.push(InfoItems.THUMBPRINT);
    } else {
      infoItems = [];
    }
    const isRange = this.pageType === PageType.RANGE;
    const cleanedRange = (this.pageRangeLabel ?? '').trim();
    const profile: any = {
      hasSelectedCert: !!this.selectCertificateType,
      selectedCertId: this.selectCertificateType ? (this.selectCertificate?.certificateHandler ?? 0) : 0,
      includeImage: !!this.radioSignatureImage,
      includeInfo,
      infoItems,
      pageType: this.pageType as unknown as PageType,
    };
    if (isRange) {
      profile.pageRange = cleanedRange;
    }
    return profile as SignatureProfile;
  }

  // VISUAL IMG
  public visualSignTypeChange(enabled: boolean): void {
    this.radioSignatureImage = enabled;
    if (!enabled) {
      this.blockImg = false;
      this._syncBlocksVisibility();
      this._updateHasChanges();
      return;
    }
    if (this.defaultImageFile) {
      this.imageSelectionChanged(this.defaultImageFile, false);
      this._syncBlocksVisibility();
      this._updateHasChanges();
      return;
    }
    this._loadSignatureImage();
    this._syncBlocksVisibility();
    this._updateHasChanges();
  }

  public imageSelectionChanged(image: File, fromServer: boolean = false): void {
    if (!this.radioSignatureImage) {
      this.blockImg = false;
      this._updateHasChanges();
      return;
    }
    if (!image && this.radioSignatureImage) {
      this.blockImg = true;
    } else if (image && this.radioSignatureImage) {
      this.defaultImageFile = image;
      if (!fromServer) {
        this._hasLocalImageChange = true;
      }
      this.imgInfo = {
        name: image.name,
        size: formatBytes(image.size, 0),
      };
      this.blockImg = false;
    } else {
      this.blockImg = false;
    }
    this._updateHasChanges();
  }

  public onFileDropped(file: File): void {
    try {
      this._validateImageFileSize(file);
      this._validateImageFileType(file);
      this._handleFileSelect(file);
    } catch (error) {
      this._notify.feedBackResult(MessageType.ERROR, error.message);
    }
  }

  private _validateImageFileSize(file: File): void {
    // SIZE VALIDATE
    if (!this._utilsSigntrust.validateFileSize(file.size, 2)) {
      throw new Error(this._translate.instant('WEB-SIGN.SIGNTRUST-LIB.ERRORS.FILESIZE2MB'));
    }
  }

  private _validateImageFileType(file: File): void {
    // TYPE VALIDATE
    if (!this._utilsSigntrust.validateFileType(file.type, this.allowedExtension)) {
      throw new Error(this._translate.instant('WEB-SIGN.SIGNTRUST-LIB.ERRORS.FILETYPEPNG'));
    }
  }

  private _handleFileSelect(file: File): void {
    // Emit image change.
    this.imageSelectionChanged(file, false);
  }
}
